import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import {
  Action,
  Badge,
  Card,
  ConfirmModal,
  EmailInput,
  Modal,
  SimpleSelect,
  TextInput,
  useToast,
} from 'corso-admin-ui';
import { CommandAdminApi, commandStoreApi, ContactTypeEnum } from 'corso-types';
import { FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import {
  DataTable,
  DataTableStatus,
  sharedTableDefinitions,
} from '~/components/dataTable';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';

type StoreContact = CommandAdminApi.ResponseBody<
  '/store/:storeId',
  'get'
>['contacts'][number];

const contactSchema =
  commandStoreApi['/store/:storeId/contact'].post.request.body;

const contactTypeOptions = Object.values(ContactTypeEnum).map((value) => ({
  value,
  label: value,
}));

const { idColumn } = sharedTableDefinitions;

const fullName = (contact: StoreContact) =>
  [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '—';

const contactColumns: ColumnDef<StoreContact>[] = [
  idColumn<StoreContact>({ meta: { align: 'left' } }),
  {
    id: 'name',
    header: 'Name',
    accessorFn: fullName,
    enableSorting: true,
  },
  { accessorKey: 'email', header: 'Email', enableSorting: true },
  { accessorKey: 'phone', header: 'Phone', enableSorting: true },
  {
    accessorKey: 'type',
    header: 'Type',
    enableSorting: true,
    cell: ({ row }) => <Badge variant="info">{row.original.type}</Badge>,
  },
];

function AddContactModal({ onClose }: { onClose: () => void }) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
  });

  const { mutate: createContact } = useMutation({
    mutationFn: api.store(storeId).createContact,
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Created contact');
    },
    onError: () => toast.show('Failed to create contact'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => createContact(body))(event).catch(console.error);
  };

  return (
    <Modal show onClose={onClose} title="Add Store Contact">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="firstName"
          label="First name"
          required
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <TextInput
          id="lastName"
          label="Last name"
          required
          {...register('lastName')}
          error={errors.lastName?.message}
        />
        <EmailInput
          id="email"
          label="Email"
          required
          {...register('email')}
          error={errors.email?.message}
        />
        <TextInput
          id="phone"
          label="Phone"
          required
          {...register('phone')}
          error={errors.phone?.message}
        />
        <Controller
          control={control}
          name="type"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Type"
              required
              options={contactTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit">
            Add
          </Action>
        </div>
      </form>
    </Modal>
  );
}

function EditContactModal({
  contact,
  onClose,
}: {
  contact: StoreContact;
  onClose: () => void;
}) {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone,
      type: contact.type,
    },
  });

  const { mutate: updateContact } = useMutation({
    mutationFn: (body: z.infer<typeof contactSchema>) =>
      api.store(storeId).updateContact(contact.id, body),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated contact');
    },
    onError: () => toast.show('Failed to update contact'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((body) => updateContact(body))(event).catch(console.error);
  };

  return (
    <Modal show onClose={onClose} title="Edit Store Contact">
      <form className="flex flex-col gap-6" onSubmit={submitHandler}>
        <TextInput
          id="firstName"
          label="First name"
          required
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <TextInput
          id="lastName"
          label="Last name"
          required
          {...register('lastName')}
          error={errors.lastName?.message}
        />
        <EmailInput
          id="email"
          label="Email"
          required
          {...register('email')}
          error={errors.email?.message}
        />
        <TextInput
          id="phone"
          label="Phone"
          required
          {...register('phone')}
          error={errors.phone?.message}
        />
        <Controller
          control={control}
          name="type"
          render={({ field, fieldState }) => (
            <SimpleSelect
              label="Type"
              required
              options={contactTypeOptions}
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <div className="flex justify-end">
          <Action variant="primary" type="submit">
            Save
          </Action>
        </div>
      </form>
    </Modal>
  );
}

export function ContactsCard() {
  const storeId = useStoreId();
  const refetch = useRefetchStore();
  const toast = useToast();
  const { contacts } = useStoreContext();
  const [showAdd, setShowAdd] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<StoreContact | null>(null);
  const [editType, setEditType] = useState<'edit' | 'delete' | null>(null);

  const closeEdit = () => {
    setContactToEdit(null);
    setEditType(null);
  };

  const { mutate: deleteContact, isPending: deletePending } = useMutation({
    mutationFn: (contactId: number) =>
      api.store(storeId).deleteContact(contactId),
    onSuccess: () => {
      closeEdit();
      refetch();
      toast.show('Deleted contact');
    },
    onError: () => toast.show('Failed to delete contact'),
  });

  const columns: ColumnDef<StoreContact>[] = [
    ...contactColumns,
    {
      id: 'actions',
      header: '',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <>
          <Action
            variant="ghost"
            icon="edit"
            accessibilityLabel="Edit Contact"
            onClick={() => {
              setContactToEdit(row.original);
              setEditType('edit');
            }}
          />
          <Action
            variant="ghost"
            icon="remove"
            accessibilityLabel="Delete Contact"
            onClick={() => {
              setContactToEdit(row.original);
              setEditType('delete');
            }}
          />
        </>
      ),
    },
  ];

  return (
    <Card>
      <Card.Line>
        <Card.Heading>Contacts</Card.Heading>
        <Action
          variant="ghost"
          icon="add"
          onClick={() => setShowAdd(true)}
          accessibilityLabel="Add Contact"
        />
      </Card.Line>

      {contacts.length > 0 ? (
        <DataTable data={contacts} columns={columns} pageSize={10} filterable />
      ) : (
        <DataTableStatus
          icon="message"
          title="No store contacts"
          details="Add a contact to loop non-Corso people in on this store."
        />
      )}

      {showAdd && <AddContactModal onClose={() => setShowAdd(false)} />}

      {contactToEdit && editType === 'edit' && (
        <EditContactModal contact={contactToEdit} onClose={closeEdit} />
      )}

      <ConfirmModal
        show={editType === 'delete'}
        title="Delete contact"
        prompt="Delete this store contact? This cannot be undone."
        confirmText="Delete"
        variant="destructive"
        loading={deletePending}
        onCancel={closeEdit}
        onConfirm={() => {
          if (!contactToEdit) return;
          deleteContact(contactToEdit.id);
        }}
      />
    </Card>
  );
}
